import { useCallback, useState } from "react";
import { useFormContext, type FieldPath, type FieldValues } from "react-hook-form";
import { ValidationError, type ObjectSchema } from "yup";

interface UseStepperFormOptions {
  stepIds: number[];
  stepSchemas: Partial<Record<number, ObjectSchema<FieldValues>>>;
  onStepIndexChange?: (index: number) => void;
}

interface UseStepperFormResult {
  currentStepIndex: number;
  currentStepId: number;
  goNext: () => Promise<boolean>;
  goBack: () => void;
  goToStep: (stepId: number) => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export function useStepperForm<TFormInputs extends FieldValues>({
  stepIds,
  stepSchemas,
  onStepIndexChange,
}: UseStepperFormOptions): UseStepperFormResult {
  const { getValues, setError, clearErrors, setFocus } = useFormContext<TFormInputs>();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStepId = stepIds[currentStepIndex] ?? stepIds[0] ?? 0;

  const updateStep = useCallback(
    (nextIndex: number) => {
      setCurrentStepIndex(nextIndex);
      onStepIndexChange?.(nextIndex);
      window.scrollTo({ top: 0 });
    },
    [onStepIndexChange],
  );

  const goNext = useCallback(async (): Promise<boolean> => {
    const id = stepIds[currentStepIndex];
    if (id === undefined) return false;
    const schema = stepSchemas[id];
    if (!schema) {
      clearErrors();
      updateStep(currentStepIndex + 1);
      return true;
    }
    try {
      await schema.validate(getValues(), { abortEarly: false });
      clearErrors();
      updateStep(currentStepIndex + 1);
      return true;
    } catch (err) {
      if (err instanceof ValidationError) {
        clearErrors();
        err.inner.forEach(error => {
          if (error.path) {
            setError(error.path as FieldPath<TFormInputs>, { type: "validation", message: error.message });
          }
        });
        // Yup with abortEarly:false reports nested errors in Promise.all resolution
        // order, not array order. Sort error paths so we attempt focus on the
        // topmost/leftmost field first (numeric on array indices, lexical otherwise).
        const sortedErrors = [...err.inner]
          .filter(e => e.path)
          .map(e => ({ error: e, normalizedPath: e.path!.replace(/\[(\d+)\]/g, ".$1") }))
          .sort((a, b) => {
            const aParts = a.normalizedPath.split(".");
            const bParts = b.normalizedPath.split(".");
            for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
              const ap = aParts[i];
              const bp = bParts[i];
              if (ap === bp) continue;
              if (ap === undefined) return -1;
              if (bp === undefined) return 1;
              const aNum = /^\d+$/.test(ap);
              const bNum = /^\d+$/.test(bp);
              if (aNum && bNum) return parseInt(ap, 10) - parseInt(bp, 10);
              return ap.localeCompare(bp);
            }
            return 0;
          });

        // Try each error in order until setFocus actually lands on a registered field.
        // Some schema paths have no UI binding (e.g. providerId is set programmatically
        // via setValue but never wrapped in a FormField), so setFocus silently no-ops.
        const before = document.activeElement;
        for (const { normalizedPath } of sortedErrors) {
          setFocus(normalizedPath as FieldPath<TFormInputs>);
          if (document.activeElement !== before) break;
        }
      }
      return false;
    }
  }, [stepIds, currentStepIndex, stepSchemas, getValues, setError, clearErrors, setFocus, updateStep]);

  const goBack = useCallback(() => {
    if (currentStepIndex === 0) return;
    clearErrors();
    updateStep(currentStepIndex - 1);
  }, [currentStepIndex, clearErrors, updateStep]);

  const goToStep = useCallback(
    (stepId: number) => {
      const idx = stepIds.indexOf(stepId);
      if (idx === -1 || idx > currentStepIndex) return;
      clearErrors();
      updateStep(idx);
    },
    [stepIds, currentStepIndex, clearErrors, updateStep],
  );

  return {
    currentStepIndex,
    currentStepId,
    goNext,
    goBack,
    goToStep,
    isFirstStep: currentStepIndex === 0,
    isLastStep: currentStepIndex === stepIds.length - 1,
  };
}
